/* Adds accessible controls while leaving pricing, inventory and checkout logic to the existing components. */
(() => {
  'use strict';
  const visible = el => {
    if (!el) return false;
    const s = getComputedStyle(el), r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && s.display !== 'none' && s.visibility !== 'hidden';
  };
  const set = (el, name, value) => { if (el.getAttribute(name) !== value) el.setAttribute(name, value); };
  const keyboardButtons = new WeakSet();
  const disabledPaymentLinks = new Map();
  const menuBranchState = new Map();
  const submenuState = new WeakMap();
  let accordionId = 0;
  const shadows = new WeakSet();
  let currentDialog = null, returnFocus = null, lastControl = null;
  const savedBackground = new Map();
  const savedDialogAttributes = new WeakMap();
  document.addEventListener('click', event => {
    const control = event.target.closest('button,a,[role="button"]');
    if (control && !control.closest('.m-modal--wrapper,[data-search-popup]')) lastControl = control;
  }, true);
  const restoreBackground = () => {
    for (const [el, old] of savedBackground) {
      el.inert = old.inert;
      if (old.hidden === null) el.removeAttribute('aria-hidden'); else el.setAttribute('aria-hidden', old.hidden);
    }
    savedBackground.clear();
  };
  const dialogRoots = () => {
    const roots = Array.from(document.querySelectorAll('.m-modal--wrapper.m-open-modal')).filter(visible);
    const menu = document.querySelector('#m-menu-drawer.open');
    if (menu && visible(menu)) roots.unshift(menu.closest('m-header') || menu);
    document.querySelectorAll('.facest-filters-section.sidebar-open .m-sidebar').forEach(el => { if (visible(el)) roots.push(el); });
    document.querySelectorAll('.m-sortby-mobile--wrapper').forEach(el => { if (visible(el) && el.style.display === 'block') roots.push(el); });
    const search = document.querySelector('[data-search-popup]');
    if (search && visible(search) && search.style.opacity === '1') roots.unshift(search);
    document.querySelectorAll('[data-kl-scroll-locking-modal]').forEach(el => { if (visible(el)) roots.push(el); });
    return roots;
  };
  const syncDialog = () => {
    const roots = dialogRoots(), next = roots[roots.length - 1] || null;
    if (next !== currentDialog) {
      restoreBackground();
      const previous = currentDialog;
      if (previous && savedDialogAttributes.has(previous)) {
        for (const [name, value] of Object.entries(savedDialogAttributes.get(previous))) { if (value === null) previous.removeAttribute(name); else previous.setAttribute(name, value); }
      }
      currentDialog = next;
      if (next) {
        if (!savedDialogAttributes.has(next)) savedDialogAttributes.set(next, Object.fromEntries(['role','aria-modal','aria-label','aria-owns'].map(name => [name,next.getAttribute(name)])));
        returnFocus = lastControl || document.activeElement;
        set(next, 'role', 'dialog'); set(next, 'aria-modal', 'true');
        const heading = next.querySelector('h1,h2,h3');
        const menu = next.querySelector('#m-menu-drawer.open');
        const label = next.matches('[data-search-popup]') ? 'Search products' : next.matches('[data-kl-scroll-locking-modal]') ? 'Newsletter signup' : menu ? 'Navigation menu' : next.matches('.m-sidebar') ? 'Filter products' : next.matches('.m-sortby-mobile--wrapper') ? 'Sort products' : (heading?.textContent.trim() || 'Product information');
        set(next, 'aria-label', label);
        for(let path=next;path&&path!==document.body;path=path.parentElement){if(path.inert||path.getAttribute('aria-hidden')==='true'){if(!savedBackground.has(path))savedBackground.set(path,{inert:path.inert,hidden:path.getAttribute('aria-hidden')});path.inert=false;path.removeAttribute('aria-hidden');}}
        if (menu || !next.contains(document.activeElement)) {
          const target = (menu || next).querySelector('input:not([type="hidden"]),button,a[href],select,textarea');
          if (target && visible(target)) target.focus();
        }
      } else if (previous && returnFocus?.isConnected) {
        returnFocus.focus(); returnFocus = null;
      }
    }
    if (!currentDialog) return;
    const floating = currentDialog.matches('[data-search-popup]') ? document.querySelector('#snize-instant-search-results') : null;
    if (floating) set(currentDialog, 'aria-owns', floating.id);
    for (let activeBranch = currentDialog; activeBranch && activeBranch !== document.body; activeBranch = activeBranch.parentElement) {
      const parent = activeBranch.parentElement;
      if (!parent) break;
      for (const child of parent.children) {
        if (/^(SCRIPT|STYLE|LINK)$/.test(child.tagName) || child === activeBranch || (floating && (child === floating || child.contains(floating)))) continue;
        if (!savedBackground.has(child)) savedBackground.set(child, { inert: child.inert, hidden: child.getAttribute('aria-hidden') });
        child.inert = true; set(child, 'aria-hidden', 'true');
      }
    }
  };
  document.addEventListener('keydown', event => {
    if(event.key==='Escape' && currentDialog?.matches('[data-kl-scroll-locking-modal]')){const close=currentDialog.querySelector('button[aria-label*="Close"],button[aria-label*="close"]');if(close){event.preventDefault();event.stopImmediatePropagation();close.click();return;}}
    if (event.key === 'Escape' && currentDialog) {
      const close = currentDialog.matches('.m-sidebar') ? currentDialog.querySelector('.m-sidebar--close') : currentDialog.matches('.m-sortby-mobile--wrapper') ? currentDialog.querySelector('.m-sortby-mobile--close') : null;
      if (close) close.click();
    }
    if (event.key !== 'Tab' || !currentDialog) return;
    const floating = currentDialog.matches('[data-search-popup]') ? document.querySelector('#snize-instant-search-results') : null;
    const roots = [currentDialog, floating].filter(Boolean);
    const controls = roots.flatMap(root => Array.from(root.querySelectorAll('a[href],button,input:not([type="hidden"]),select,textarea,[tabindex]'))).filter(el => el.tabIndex >= 0 && !el.disabled && visible(el));
    if (!controls.length) return;
    const first = controls[0], last = controls[controls.length - 1], active = document.activeElement;
    if (event.shiftKey && (active === first || !roots.some(root => root.contains(active)))) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (active === last || !roots.some(root => root.contains(active)))) { event.preventDefault(); first.focus(); }
  }, true);
  const setupAnnouncement = bar => {
    const slider = bar.slider, button = bar.querySelector('[data-announcement-toggle]');
    if (!slider || !button || bar.dataset.a11yRotationReady) return;
    bar.dataset.a11yRotationReady = 'true';
    let paused = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const render = () => { button.textContent = paused ? 'Play' : 'Pause'; set(button, 'aria-label', paused ? 'Play announcements' : 'Pause announcements'); };
    const pause = () => { paused = true; slider.autoplay?.stop(); render(); };
    button.addEventListener('click', () => { paused = !paused; if (paused) slider.autoplay?.stop(); else slider.autoplay?.start(); render(); });
    bar.addEventListener('focusin', event => { if (event.target !== button) pause(); });
    slider.on('autoplayStart', () => { if (paused) slider.autoplay?.stop(); });
    slider.on('transitionEnd', () => { if (paused) slider.autoplay?.stop(); });
    if (paused) slider.autoplay?.stop(); render();
    matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', event => { if (event.matches) pause(); });
  };
  const apply = () => {
    for (const [el, old] of menuBranchState) { el.inert=old.inert; if(old.hidden===null)el.removeAttribute('aria-hidden');else set(el,'aria-hidden',old.hidden); }
    menuBranchState.clear();
    const menuDrawer=document.querySelector('#m-menu-drawer');
    if(menuDrawer){const open=menuDrawer.classList.contains('open');menuDrawer.inert=!open;set(menuDrawer,'aria-hidden',open?'false':'true');}
    document.querySelectorAll('.m-megamenu-mobile').forEach(panel => {
      const drawer = panel.closest('#m-menu-drawer');
      const viewport = drawer?.querySelector('.m-menu-drawer__wrapper');
      const panelRect = panel.getBoundingClientRect();
      const viewportRect = viewport?.getBoundingClientRect();
      const open = !!(drawer?.classList.contains('open') && viewportRect && Math.min(panelRect.right, viewportRect.right) - Math.max(panelRect.left, viewportRect.left) > panelRect.width / 2);
      const wasOpen=submenuState.get(panel);submenuState.set(panel,open);
      panel.inert = !open; set(panel, 'aria-hidden', open ? 'false' : 'true');
      const item = panel.closest('.m-menu-mobile__item');
      const trigger = item && Array.from(item.querySelectorAll('[data-toggle-submenu]')).find(el => !panel.contains(el) && !el.classList.contains('m-menu-mobile__back-button'));
      if (trigger) { set(trigger, 'aria-expanded', open ? 'true' : 'false'); if (!panel.id) panel.id='accessible-mobile-submenu-'+(++accordionId); set(trigger,'aria-controls',panel.id); }
      if(menuDrawer?.classList.contains('open') && wasOpen!==undefined && wasOpen!==open){const target=open?panel.querySelector('.m-menu-mobile__back-button,button,a[href]'):trigger;if(target)target.focus();}
    });
    const menuRoot=menuDrawer?.querySelector('.m-menu-mobile');
    const openPanels=menuRoot?Array.from(menuRoot.querySelectorAll('.m-megamenu-mobile[aria-hidden="false"]')):[];
    const activePanel=openPanels[openPanels.length-1];
    if(activePanel && menuDrawer.classList.contains('open'))for(let branch=activePanel;branch&&branch!==menuRoot;branch=branch.parentElement){for(const sibling of branch.parentElement?.children||[]){if(sibling===branch||/^(SCRIPT|STYLE)$/.test(sibling.tagName))continue;menuBranchState.set(sibling,{inert:sibling.inert,hidden:sibling.getAttribute('aria-hidden')});sibling.inert=true;set(sibling,'aria-hidden','true');}}
    document.querySelectorAll('.shopify-payment-button').forEach(wrapper => {
      const disabled = wrapper.hasAttribute('disabled');
      if (disabled) {
        set(wrapper, 'aria-disabled', 'true');
        wrapper.querySelectorAll('a[href]').forEach(link => {
          if (!disabledPaymentLinks.has(link)) disabledPaymentLinks.set(link, { tab: link.getAttribute('tabindex'), aria: link.getAttribute('aria-disabled') });
          set(link, 'aria-disabled', 'true'); set(link, 'tabindex', '-1');
        });
      } else if (wrapper.getAttribute('aria-disabled') === 'true') wrapper.removeAttribute('aria-disabled');
    });
    for (const [link, old] of disabledPaymentLinks) {
      if (!link.closest('.shopify-payment-button[disabled]')) {
        if (old.tab === null) link.removeAttribute('tabindex'); else set(link, 'tabindex', old.tab);
        if (old.aria === null) link.removeAttribute('aria-disabled'); else set(link, 'aria-disabled', old.aria);
        disabledPaymentLinks.delete(link);
      }
    }
    document.querySelectorAll('.accordian__header').forEach(header => {
      set(header, 'role', 'button'); set(header, 'tabindex', '0');
      set(header, 'aria-expanded', header.classList.contains('active') ? 'true' : 'false');
      const panel = header.nextElementSibling;
      if (panel) { if (!panel.id) panel.id = 'accessible-product-detail-'+(++accordionId); set(header, 'aria-controls', panel.id); }
      if (!keyboardButtons.has(header)) { keyboardButtons.add(header); header.addEventListener('keydown', event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); header.click(); } }); }
    });
    document.querySelectorAll('.m-modal--close').forEach(el => { set(el, 'aria-label', 'Close dialog'); set(el, 'type', 'button'); });
    document.querySelectorAll('.deferred-media__poster').forEach(el => { if (!el.getAttribute('aria-label')?.trim()) set(el, 'aria-label', 'Play product video'); });
    document.querySelectorAll('.gfbFrequentBoughtWidgetProductImageEle').forEach(image => {
      if (image.getAttribute('alt')?.trim()) return;
      const card = image.closest('.gfbFrequentBoughtWidgetProductContainer');
      const title = card?.querySelector('[class*="ProductName"],[class*="ProductTitle"],a[href*="/products/"]');
      if (title?.textContent.trim()) set(image, 'alt', title.textContent.trim());
    });
    document.querySelectorAll('.gfbDropdownMenuItems').forEach(el => { set(el, 'tabindex', '0'); set(el, 'aria-label', 'Product options'); });
    document.querySelectorAll('input.gfbFrequentBoughtQuanityBox').forEach(el => {
      const title=el.closest('.gfbFrequentBoughtWidgetProductContainer')?.querySelector('.gfbFrequentBoughtWidgetProductTitle')?.textContent.trim();
      set(el, 'aria-label', title ? 'Quantity for '+title : 'Product quantity');
    });
    document.querySelectorAll('.gfbFrequentBoughtAddToCartButton').forEach(el => {
      if (el.tagName === 'BUTTON') return;
      set(el, 'role', 'button'); set(el, 'tabindex', '0');
      if (!keyboardButtons.has(el)) { keyboardButtons.add(el); el.addEventListener('keydown', event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); el.click(); } }); }
    });
    const topLink = document.querySelector('a.gq_45zabV5[href="#scroll-to-top"]');
    if (topLink) set(topLink, 'aria-label', 'Back to top');
    document.querySelectorAll('m-announcement-bar').forEach(setupAnnouncement);
    document.querySelectorAll('shop-follow-button').forEach(host => {
      const root=host.shadowRoot;if(!root||shadows.has(root))return;shadows.add(root);
      const style=document.createElement('style');style.textContent='button{background-color:#5433eb!important}.text-white{color:#fff!important}';root.appendChild(style);
    });
    document.querySelectorAll('#shadow-host').forEach(host => {
      const root = host.shadowRoot;
      if (!root || shadows.has(root)) return;
      shadows.add(root);
      const style = document.createElement('style');
      style.textContent = '[class*="text-[var(--primary-color)]"],[class*="text-[var(--secondary-color)]"],.activePlanTxt,.notActivePlanTxt{color:#1d1d1f!important}.hero-sec-content-btn-primery,button[class*="bg-[var(--primary-color)]"],.get-offer-div{color:#1d1d1f!important}a:focus-visible,button:focus-visible{outline:2px solid #1d1d1f;outline-offset:3px}';
      root.appendChild(style);
    });
    syncDialog();
  };
  let scheduled = false;
  const schedule = () => { if (!scheduled) { scheduled = true; requestAnimationFrame(() => { scheduled = false; apply(); }); } };
  const start = () => {
    apply();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'open', 'disabled'] });
    window.addEventListener('load', schedule, { once: true });
    document.addEventListener('scroll', event => { if (event.target.matches?.('.m-menu-drawer__wrapper')) schedule(); }, true);
    window.addEventListener('resize', schedule);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();
